import { useEffect, useMemo, useState } from "react";
import { DropdownInfoFactory } from "../../../factories";
import { OrgMembershipsFactory } from "../../../factories/orgMemberships";
import { IdentifierWithChildren, WaardelijstFactory } from "../../../factories/waardelijst";
import { Identifier } from "../../../models";
import { DossierType } from "../../../models/dossier-metadata";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { clearDocumentState } from "../../../store/reducers/document-metadata";
import { clearDossierState, validate } from "../../../store/reducers/dossier-metadata";
import { resetStepperState, setNextStep } from "../../../store/reducers/stepper";
import { selectDossier } from "../../../store/selectors";
import { DossierFormConfig } from "../../../utils/DossierFormConfig";
import { filterActievePublishers, useOrganisaties } from "../../../utils/OrganisatiesHelper";
import { SpacerAtom } from "../../atoms";
import { ButtonMolecule, FoldableMolecule, FormMolecule, PopupAnnuleerDossierMolecule } from "../../molecules";
import { FormFieldRenderer } from "../../molecules/formfields";

export function DossierFormulierOrganism() {
  const dispatch = useAppDispatch();
  const root = useAppSelector(selectDossier);
  const { mode, data, localValidationError } = useAppSelector((s) => s.dossier);
  const { publicerendePartijen } = useOrganisaties();
  const [popupAnnuleren, setPopupAnnuleren] = useState(false);

  const dossiertype: Identifier[] = Object.values(DossierType).map((v) => ({ id: v, label: v }) as Identifier);
  const factories = useMemo(
    () => ({
      waardelijstFactory: new WaardelijstFactory(),
      dropdownInfo: new DropdownInfoFactory(),
      orgMemberships: new OrgMembershipsFactory(),
    }),
    []
  );
  const AuthOrganisationsList = factories.orgMemberships.create();
  const requiredRefs: HTMLInputElement[] = [];

  const actievePublishers = useMemo(() => {
    const lijst = filterActievePublishers(publicerendePartijen, AuthOrganisationsList);
    return lijst;
  }, [publicerendePartijen]);

  const actievePublisher: boolean =
    data.dossierHouder?.id === undefined ||
    publicerendePartijen.some(
      (organisatie: IdentifierWithChildren) =>
        organisatie.id === data.dossierHouder?.id && organisatie.label === data.dossierHouder?.label
    );
  const disableForm: boolean = !actievePublisher;

  const formConfig = DossierFormConfig({
    dispatch,
    disableForm,
    factories,
    requiredRefs,
    mode,
    data: data,
    actievePublishers,
    dossiertype,
  });

  useEffect(() => {
    if (localValidationError) {
      requiredRefs.forEach((element: HTMLInputElement | HTMLButtonElement, index: number) => {
        if (index === 0) element.click();

        element.focus();
        element.blur();
      });
    }
  }, [localValidationError]);

  const onUpload = () => {
    if (mode === "create") {
      dispatch(validate())
        .unwrap()
        .then(() => dispatch(setNextStep()))
        .catch((err) => {});
    }
  };

  const rightButton = () => {
    return (
      <ButtonMolecule
        text={"Voeg documenten toe"}
        id={"voeg_documenten_toe"}
        title={"Voeg documenten toe."}
        onClick={onUpload}
        type={"primary"}
      />
    );
  };

  const leftButton = () => {
    return (
      <ButtonMolecule
        text={"Dossier aanmaken annuleren "}
        id={"annuleer_dossier_maken"}
        title={"Dossier aanmaken annuleren."}
        onClick={() => {
          setPopupAnnuleren(true);
        }}
        type={"default"}
      />
    );
  };

  return (
    <>
      {popupAnnuleren && (
        <PopupAnnuleerDossierMolecule
          close={() => setPopupAnnuleren(false)}
          dossierAnnuleren={() => {
            dispatch(resetStepperState());
            dispatch(clearDocumentState());
            dispatch(clearDossierState());
          }}
        />
      )}
      <FormMolecule key={root.formUniqueKey} rightButton={rightButton()} leftButton={leftButton()}>
        {formConfig.fields.required.map((field) => {
          const renderNull = field.type === "custom" && field.render() === null;
          return (
            <div key={field.id}>
              <FormFieldRenderer key={field.id} field={field} />
              {!renderNull && <SpacerAtom space={4} />}
            </div>
          );
        })}

        <FoldableMolecule label={"Optionele velden"} defaultFolded={false}>
          <SpacerAtom space={4} />

          {formConfig.fields.optional.map((field) => (
            <div key={field.id}>
              <FormFieldRenderer key={field.id} field={{ ...field, required: false }} />
              <SpacerAtom space={4} />
            </div>
          ))}
        </FoldableMolecule>
      </FormMolecule>
    </>
  );
}
